import { DatePicker } from "@mui/x-date-pickers";
import { useState } from "react";
import { Autocomplete, Button, Checkbox, FormControl, FormHelperText, InputLabel, MenuItem, Select, Tooltip } from "@mui/material";
import dayjs from "dayjs";

import { IMissingDataForm } from "../types";
import { useMissingData } from "./context";
import { Subtitle, Button as CustomButton } from "../styled";
import { TextField } from "./styled";

import { addressOptions, locationOptions } from "./helper";
import { cpfMask } from "utils/cpf";
import { useAlert } from "contexts/alert";
import { formatCNPJ } from "utils/is-valid-cnpj";
import { countryOptions, stateOptions } from "v2/pages/Workspace/Activity/ManualInclusion/ManualInclusion.utils";
import {
  GENDER_OPTIONS,
  RACE_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  EDUCATION_LEVEL_OPTIONS,
} from "v2/constants/esocial-options";

export function StepEmployee() {
  const { data, config, handleNextStep, handleUpdateData } = useMissingData();
  const { openAlert, closeAlert } = useAlert();

  const [isEditing, setIsEditing] = useState(false);
  const [editingData, setEditingData] = useState(data);
  const [editingDataErrors, setEditingDataErrors] = useState<Record<string, string>>({});

  // Função para mapear as chaves da configuração para as chaves do formulário
  function getFieldKey(configKey: string): string | null {
    const keyMapping: Record<string, string> = {
      "name": "name",
      "taxPayerId": "tax_payer_id", 
      "legalEntityId": "cnpj",
      "motherName": "mother_name",
      "nationalIdentityNumber": "registration_number",
      "birthDate": "birth_date",
      "gender": "gender_id",
      "maritalStatus": "marital_status_id",
      "phoneNumber": "phoneNumber",
      "race": "race",
      "educationLevel": "educationLevel",
      "nationalityCountry": "nationalityCountry",
      "birthCountry": "birthCountry",
      "birthState": "birthState",
      "weddingDate": "weddingDate",
      "nationalIdentityEmissor": "nationalIdentityEmissor",
      "nationalIdentityExpeditionDate": "nationalIdentityExpeditionDate",
      "jobTitle": "jobTitle",
      "paymentFrequency": "paymentFrequency",
      "grossPayment": "grossPayment",
      "cbo": "cbo",
      "zipCode": "zip_code",
      "addressType": "address_type",
      "address": "address",
      "addressNumber": "address_number",
      "complement": "complement",
      "neighborhood": "neighborhood",
      "state": "state_id",
      "city": "city_id"
    };
    
    return keyMapping[configKey] || null;
  }

  function handleNext() {
    /** Regras de validação. */
    let rules = {};
    const hasSpecialCharacters = /[^a-zA-Z0-9À-ÿ ]/;
    const hasLetterBetweenSpaces = /\s[a-zA-Z]\s/;
    const alwaysMandatoryFields = ["name", "legalEntityId", "taxPayerId"];

    /** Benefit rules - sempre obrigatórios */
    if (!editingData.employee_product_type.length) rules = { ...rules, ["employee_product_type"]: "Campo obrigatório." };
    if (!editingData.dependent_product_type.length) rules = { ...rules, ["dependent_product_type"]: "Campo obrigatório." };

    config.forEach(field => {
      if (!!field.visible) {
        /** Regra de campo obrigatório é aplicada conforme o que foi especificado na configuração. */
        if (!!field.required) {
          const fieldKey = getFieldKey(field.key);
          if (fieldKey && !editingData?.[fieldKey as keyof typeof editingData]) {
            // CNPJ só é obrigatório se for PJ
            if (field.key === "legalEntityId" && !data.isPj) {
              return; // Pula validação se não for PJ
            }
            rules = { ...rules, [fieldKey]: "Campo obrigatório." };
          }
        }

        /** Campos que sempre serão mandatórios independente da regra estabelecida. */
        if (alwaysMandatoryFields.includes(field.key)) {
          const fieldKey = getFieldKey(field.key);
          if (fieldKey && !editingData?.[fieldKey as keyof typeof editingData]) {
            // CNPJ só é obrigatório se for PJ
            if (field.key === "legalEntityId" && !data.isPj) {
              return; // Pula validação se não for PJ
            }
            rules = { ...rules, [fieldKey]: "Campo obrigatório." };
          }
        }

        /** Independente de ser obrigatório ou não, validações que acontecem pra campos específicos caso esteja editando eles. */
        if (field.key === "taxPayerId" && !!editingData?.tax_payer_id && (editingData.tax_payer_id.length !== 11 || isNaN(Number(editingData.tax_payer_id)))) rules = { ...rules, ["tax_payer_id"]: "CPF inválido." };
        if (field.key === "legalEntityId" && !!data.isPj && !!editingData?.cnpj && (editingData.cnpj.length !== 14 || isNaN(Number(editingData.cnpj)))) rules = { ...rules, ["cnpj"]: "CNPJ inválido." };
        if (field.key === "name" && !!editingData?.name && hasSpecialCharacters.test(editingData.name)) rules = { ...rules, ["name"]: "Não permitido caracteres especiais." };
        if (field.key === "name" && !!editingData?.name && hasLetterBetweenSpaces.test(editingData.name)) rules = { ...rules, ["name"]: "Não permitido abreviações." };
        if (field.key === "motherName" && !!editingData?.mother_name && hasSpecialCharacters.test(editingData.mother_name)) rules = { ...rules, ["mother_name"]: "Não permitido caracteres especiais." };
        if (field.key === "motherName" && !!editingData?.mother_name && hasLetterBetweenSpaces.test(editingData.mother_name)) rules = { ...rules, ["mother_name"]: "Não permitido abreviações." };
        if (field.key === "nationalIdentityNumber" && !!editingData?.registration_number && isNaN(Number(editingData.registration_number))) rules = { ...rules, ["registration_number"]: "RG inválido." };
      }
    });

    if (Object.entries(rules).length > 0) {
      setEditingDataErrors(rules);
      console.log("!!!!!!", rules)
      return openAlert('Verifique se os campos preenchidos estão corretos.', 'error');
    }

    /** Decide se pula próximo step ou não de acordo se possui dependentes. */
    if (editingData.dependent_product_type.includes("NONE")) return handleNextStep(2);

    handleNextStep();
  }

  function handleEdit() {
    closeAlert();
    setIsEditing(true);
  }

  function handleCancel() {
    setEditingData({ ...data });
    setIsEditing(false);
  }

  function handleSave() {
    handleUpdateData(editingData);
    setIsEditing(false);
  }

  function handleEditTitular(payload: Partial<IMissingDataForm>) {
    setEditingData((prev) => ({ ...prev, ...payload }));
  }

  const formatCurrency = (value: string): string => {
    // Remove tudo que não for número
    const onlyNumbers = value.replace(/\D/g, '');
    
    if (onlyNumbers === '') return '';
    
    // Converte para número e divide por 100 para ter centavos
    const numberValue = parseInt(onlyNumbers, 10) / 100;
    
    // Formatar como moeda brasileira com símbolo R$
    const formatted = numberValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    
    return `R$ ${formatted}`;
  };


  // Seguindo o padrão do ViewEditingData.tsx
  const alwaysMandatoryFields = ["name", "legalEntityId", "taxPayerId"];
  const visibleFields = [...config.filter(field => !!field.visible).map(field => field.key), ...alwaysMandatoryFields];
  const requiredFields = [...config.filter(field => !!field.required).map(field => field.key), ...alwaysMandatoryFields];

  console.log("!!!!!!!!", config)

  return (
    <>
      <div style={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginTop: "20px" }}>
        <Subtitle>Dados do titular</Subtitle>
        <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
          <Button variant="outlined" size="small" disabled={isEditing} onClick={handleEdit}>Editar dados</Button>
        </div>
      </div>
      {!!data.isPj && (
        <>
          <span>Dados do PJ</span>
          <div style={{ display: "flex", flexDirection: "row", gap: "24px", flexWrap: "wrap" }}>
            {visibleFields.includes("legalEntityId") && (
              <TextField
                label={`CNPJ da empresa do beneficiário${requiredFields.includes("legalEntityId") ? '*' : ''}`}
                value={editingData?.cnpj ? formatCNPJ(editingData.cnpj, false) : ""}
                onChange={(e) => handleEditTitular({ ...editingData, cnpj: e.target.value.replace(/\D/g, '').substring(0, 14) })}
                disabled={!isEditing}
                error={!!editingDataErrors["cnpj"]}
                helperText={editingDataErrors["cnpj"]}
              />
            )}
          </div>
        </>
      )}
      <span>Dados pessoais</span>
      <div style={{ display: "flex", flexDirection: "row", gap: "24px", flexWrap: "wrap" }}>
        {visibleFields.includes("taxPayerId") && (
          <Tooltip title="Não é possível editar o CPF." placement="top" arrow>
            <TextField
              label={`CPF${requiredFields.includes("taxPayerId") ? '*' : ''}`}
              value={cpfMask(editingData.tax_payer_id)}
              onChange={(e) => handleEditTitular({ ...editingData, tax_payer_id: e.target.value.replace(/\D/g, '').substring(0, 11) })}
              /** CPF estará sempre disabled. Não é possível editar o CPF */
              disabled={true}
              error={!!editingDataErrors["tax_payer_id"]}
              helperText={editingDataErrors["tax_payer_id"]}
            />
          </Tooltip>
        )}
        {visibleFields.includes("name") && (
          <TextField
            label={`Nome completo sem abreviações${requiredFields.includes("name") ? '*' : ''}`}
            value={editingData.name}
            onChange={(e) => handleEditTitular({ ...editingData, name: e.target.value })}
            disabled={!isEditing}
            error={!!editingDataErrors["name"]}
            helperText={editingDataErrors["name"]}
          />
        )}
        {visibleFields.includes("nationalIdentityNumber") && (
          <TextField
            label={`RG (apenas números)${requiredFields.includes("nationalIdentityNumber") ? '*' : ''}`}
            value={editingData.registration_number}
            onChange={(e) => handleEditTitular({ ...editingData, registration_number: e.target.value.replace(/\D/g, '') })}
            disabled={!isEditing}
            error={!!editingDataErrors["registration_number"]}
            helperText={editingDataErrors["registration_number"]}
          />
        )}
        {visibleFields.includes("birthDate") && (
          <DatePicker
            label={`Data de nascimento${requiredFields.includes("birthDate") ? '*' : ''}`}
            format="DD / MM / YYYY"
            value={dayjs(editingData.birth_date)}
            onChange={(e) => handleEditTitular({ ...editingData, birth_date: e?.format("YYYY-MM-DD") })}
            slotProps={{ textField: { error: !editingData.birth_date, style: { minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" } }}}
            disabled={!isEditing}
          />
        )}
        {visibleFields.includes("gender") && (
          <FormControl disabled={!isEditing} fullWidth style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }} error={!!editingDataErrors["gender_id"]}>
            <InputLabel>{`Gênero${requiredFields.includes("gender") ? '*' : ''}`}</InputLabel>
            <Select
              label={`Gênero${requiredFields.includes("gender") ? '*' : ''}`}
              value={editingData.gender_id}
              onChange={(e) => handleEditTitular({ ...editingData, gender_id: e.target.value })}
            >
              {GENDER_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
            {!!editingDataErrors["gender_id"] && <FormHelperText error>{editingDataErrors["gender_id"]}</FormHelperText>}
          </FormControl>
        )}
        {visibleFields.includes("maritalStatus") && (
          <FormControl disabled={!isEditing} fullWidth style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }} error={!!editingDataErrors["marital_status_id"]}>
            <InputLabel>{`Estado civil${requiredFields.includes("maritalStatus") ? '*' : ''}`}</InputLabel>
            <Select
              label={`Estado civil${requiredFields.includes("maritalStatus") ? '*' : ''}`}
              value={editingData.marital_status_id}
              onChange={(e) => handleEditTitular({ ...editingData, marital_status_id: e.target.value })}
            >
              {MARITAL_STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
            {!!editingDataErrors["marital_status_id"] && <FormHelperText error>{editingDataErrors["marital_status_id"]}</FormHelperText>}
          </FormControl>
        )}
        {visibleFields.includes("motherName") && (
          <TextField
            label={`Nome completo da mãe sem abreviações${requiredFields.includes("motherName") ? '*' : ''}`}
            value={editingData.mother_name}
            onChange={(e) => handleEditTitular({ ...editingData, mother_name: e.target.value })}
            disabled={!isEditing}
            error={!!editingDataErrors["mother_name"]}
            helperText={editingDataErrors["mother_name"]}
          />
        )}
        {visibleFields.includes("phoneNumber") && (
          <TextField
            label={`Telefone${requiredFields.includes("phoneNumber") ? '*' : ''}`}
            disabled={!isEditing}
            value={editingData.phoneNumber || ""}
            onChange={(e) => handleEditTitular({ ...editingData, phoneNumber: e.target.value})}
            error={!!editingDataErrors["phoneNumber"]}
            helperText={editingDataErrors["phoneNumber"]}
          />
        )}
        {visibleFields.includes("race") && (
          <FormControl fullWidth disabled={!isEditing} style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }} error={!!editingDataErrors["race"]}>
            <InputLabel>{`Raça${requiredFields.includes("race") ? '*' : ''}`}</InputLabel>
            <Select
              label={`Raça${requiredFields.includes("race") ? '*' : ''}`}
              value={editingData.race || ""}
              onChange={(e) => handleEditTitular({ ...editingData, race: e.target.value})}
            >
              {RACE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
            {!!editingDataErrors["race"] && <FormHelperText error>{editingDataErrors["race"]}</FormHelperText>}
          </FormControl>
        )}
        {visibleFields.includes("educationLevel") && (
          <FormControl fullWidth disabled={!isEditing} style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }} error={!!editingDataErrors["educationLevel"]}>
            <InputLabel>{`Nível de escolaridade${requiredFields.includes("educationLevel") ? '*' : ''}`}</InputLabel>
            <Select
              label={`Nível de escolaridade${requiredFields.includes("educationLevel") ? '*' : ''}`}
              value={editingData.educationLevel || ""}
              onChange={(e) => handleEditTitular({ ...editingData, educationLevel: e.target.value})}
            >
              {EDUCATION_LEVEL_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
            {!!editingDataErrors["educationLevel"] && <FormHelperText error>{editingDataErrors["educationLevel"]}</FormHelperText>}
          </FormControl>
        )}
        {visibleFields.includes("nationalityCountry") && (
          <Autocomplete
            disablePortal
            disabled={!isEditing}
            value={{ value: editingData.nationalityCountry || "", label: countryOptions.find(co => co.value === editingData.nationalityCountry)?.label || "" }}
            onChange={(_, newValue) => handleEditTitular({ ...editingData, nationalityCountry: newValue?.value || ""})}
            options={countryOptions.sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' }))}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }}
            renderInput={(params) => <TextField {...params} label={`País de nacionalidade${requiredFields.includes("nationalityCountry") ? '*' : ''}`} error={!!editingDataErrors["nationalityCountry"]} helperText={editingDataErrors["nationalityCountry"]} />}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
          />
        )}
        {visibleFields.includes("birthCountry") && (
          <Autocomplete
            disablePortal
            disabled={!isEditing}
            value={{ value: editingData.birthCountry || "", label: countryOptions.find(co => co.value === editingData.birthCountry)?.label || "" }}
            onChange={(_, newValue) => handleEditTitular({ ...editingData, birthCountry: newValue?.value || ""})}
            options={countryOptions.sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' }))}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }}
            renderInput={(params) => <TextField {...params} label={`País de nascimento${requiredFields.includes("birthCountry") ? '*' : ''}`} error={!!editingDataErrors["birthCountry"]} helperText={editingDataErrors["birthCountry"]} />}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
          />
        )}
        {visibleFields.includes("birthState") && (
          <Autocomplete
            disablePortal
            disabled={!isEditing}
            value={{ value: editingData.birthState || "", label: editingData.birthState || "" }}
            onChange={(_, newValue) => handleEditTitular({ ...editingData, birthState: newValue?.value || ""})}
            options={stateOptions.sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' }))}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }}
            renderInput={(params) => <TextField {...params} label={`Estado de nascimento${requiredFields.includes("birthState") ? '*' : ''}`} error={!!editingDataErrors["birthState"]} helperText={editingDataErrors["birthState"]} />}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
          />
        )}
        {visibleFields.includes("weddingDate") && (
          <DatePicker
            label={`Data de casamento${requiredFields.includes("weddingDate") ? '*' : ''}`}
            format="DD / MM / YYYY"
            disabled={!isEditing}
            value={editingData.weddingDate ? dayjs(editingData.weddingDate) : null}
            onChange={(e) => handleEditTitular({ ...editingData, weddingDate: e?.format("YYYY-MM-DD") })}
            slotProps={{ textField: { error: !!editingDataErrors["weddingDate"], helperText: editingDataErrors["weddingDate"], style: { minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" } }}}
          />
        )}
        {/* <TextField
          label={`RG (apenas números)`}
          disabled={!isEditing}
          value={editingData.nationalIdentityNumber || ""}
          onChange={(e) => handleEditTitular({ ...editingData, nationalIdentityNumber: e.target.value.replace(/\D/g, '') })}
          error={!!editingDataErrors["nationalIdentityNumber"]}
          helperText={editingDataErrors["nationalIdentityNumber"]}
        /> */}
        {visibleFields.includes("nationalIdentityEmissor") && (
          <TextField
            label={`Órgão emissor do RG${requiredFields.includes("nationalIdentityEmissor") ? '*' : ''}`}
            disabled={!isEditing}
            value={editingData.nationalIdentityEmissor || ""}
            onChange={(e) => handleEditTitular({ ...editingData, nationalIdentityEmissor: e.target.value })}
            error={!!editingDataErrors["nationalIdentityEmissor"]}
            helperText={editingDataErrors["nationalIdentityEmissor"]}
          />
        )}
        {visibleFields.includes("nationalIdentityExpeditionDate") && (
          <DatePicker
            label={`Data de expedição do RG${requiredFields.includes("nationalIdentityExpeditionDate") ? '*' : ''}`}
            disabled={!isEditing}
            format="DD / MM / YYYY"
            value={editingData.nationalIdentityExpeditionDate ? dayjs(editingData.nationalIdentityExpeditionDate) : null}
            onChange={(e) => handleEditTitular({ ...editingData, nationalIdentityExpeditionDate: e?.format("YYYY-MM-DD") })}
            slotProps={{ textField: { error: !!editingDataErrors["nationalIdentityExpeditionDate"], helperText: editingDataErrors["nationalIdentityExpeditionDate"], style: { minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" } }}}
          />
        )}
        {visibleFields.includes("jobTitle") && (
          <TextField
            label={`Cargo${requiredFields.includes("jobTitle") ? '*' : ''}`}
            disabled={!isEditing}
            value={editingData.jobTitle || ""}
            onChange={(e) => handleEditTitular({ ...editingData, jobTitle: e.target.value })}
            error={!!editingDataErrors["jobTitle"]}
            helperText={editingDataErrors["jobTitle"]}
          />
        )}
        {visibleFields.includes("paymentFrequency") && (
          <FormControl fullWidth disabled={!isEditing} style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }} error={!!editingDataErrors["paymentFrequency"]}>
            <InputLabel>{`Periodicidade de pagamento${requiredFields.includes("paymentFrequency") ? '*' : ''}`}</InputLabel>
            <Select
              label={`Periodicidade de pagamento${requiredFields.includes("paymentFrequency") ? '*' : ''}`}
              value={editingData.paymentFrequency || ""}
              onChange={(e) => handleEditTitular({ ...editingData, paymentFrequency: e.target.value})}
            >
              <MenuItem value="1 - Por Hora">Por Hora</MenuItem>
              <MenuItem value="2 - Por Dia">Por Dia</MenuItem>
              <MenuItem value="3 - Por Semana">Por Semana</MenuItem>
              <MenuItem value="4 - Por Quinzena">Por Quinzena</MenuItem>
              <MenuItem value="5 - Por Mês">Por Mês</MenuItem>
              <MenuItem value="6 - Por Tarefa">Por Tarefa</MenuItem>
              <MenuItem value="7 - Não aplicável">Não aplicável</MenuItem>
            </Select>
            {!!editingDataErrors["paymentFrequency"] && <FormHelperText error>{editingDataErrors["paymentFrequency"]}</FormHelperText>}
          </FormControl>
        )}
        {visibleFields.includes("grossPayment") && (
          <TextField
            label={`Pagamento bruto${requiredFields.includes("grossPayment") ? '*' : ''}`}
            disabled={!isEditing}
            value={formatCurrency(editingData.grossPayment || "")}
            onChange={(e) => {
              handleEditTitular({ ...editingData, grossPayment: e.target.value })
            }}
            error={!!editingDataErrors["grossPayment"]}
            helperText={editingDataErrors["grossPayment"]}
            placeholder="R$ 0,00"
          />
        )}
        {visibleFields.includes("cbo") && (
          <TextField
            label={`CBO${requiredFields.includes("cbo") ? '*' : ''}`}
            disabled={!isEditing}
            value={editingData.cbo || ""}
            onChange={(e) => handleEditTitular({ ...editingData, cbo: e.target.value })}
            error={!!editingDataErrors["cbo"]}
            helperText={editingDataErrors["cbo"]}
          />
        )}
      </div>
      <span>Endereço</span>
      <div style={{ display: "flex", flexDirection: "row", gap: "24px", flexWrap: "wrap" }}>
        {visibleFields.includes("zipCode") && (
          <TextField
            label={`CEP${requiredFields.includes("zipCode") ? '*' : ''}`}
            value={editingData.zip_code}
            onChange={(e) => handleEditTitular({ ...editingData, zip_code: e.target.value.replace(/\D/g, '').substring(0, 8) })}
            disabled={!isEditing}
            error={!!editingDataErrors["zip_code"]}
            helperText={editingDataErrors["zip_code"]}
          />
        )}
        {visibleFields.includes("addressType") && (
          <Autocomplete
            disablePortal
            disabled={!isEditing}
            value={{ value: editingData.address_type || "", label: addressOptions.find(ao => ao.value === editingData.address_type)?.label || "" }}
            onChange={(_, newValue) => handleEditTitular({ ...editingData, address_type: newValue?.value || "" })}
            options={addressOptions.sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' }))}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }}
            renderInput={(params) => <TextField {...params} label={`Tipo do endereço${requiredFields.includes("addressType") ? '*' : ''}`} error={!!editingDataErrors["address_type"]} helperText={editingDataErrors["address_type"]} />}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
          />
        )}
        {visibleFields.includes("address") && (
          <TextField
            label={`Endereço${requiredFields.includes("address") ? '*' : ''}`}
            value={editingData.address}
            onChange={(e) => handleEditTitular({ ...editingData, address: e.target.value })}
            disabled={!isEditing}
            error={!!editingDataErrors["address"]}
            helperText={editingDataErrors["address"]}
          />
        )}
        {visibleFields.includes("addressNumber") && (
          <TextField
            label={`Número${requiredFields.includes("addressNumber") ? '*' : ''}`}
            value={editingData.address_number}
            onChange={(e) => handleEditTitular({ ...editingData, address_number: e.target.value })}
            disabled={!isEditing}
            error={!!editingDataErrors["address_number"]}
            helperText={editingDataErrors["address_number"]}
          />
        )}
        {visibleFields.includes("complement") && (
          <TextField
            label={`Complemento${requiredFields.includes("complement") ? '*' : ''}`}
            value={editingData.complement}
            onChange={(e) => handleEditTitular({ ...editingData, complement: e.target.value })}
            disabled={!isEditing}
            error={!!editingDataErrors["complement"]}
            helperText={editingDataErrors["complement"]}
          />
        )}
        {visibleFields.includes("neighborhood") && (
          <TextField
            label={`Bairro${requiredFields.includes("neighborhood") ? '*' : ''}`}
            value={editingData.neighborhood}
            onChange={(e) => handleEditTitular({ ...editingData, neighborhood: e.target.value })}
            disabled={!isEditing}
            error={!!editingDataErrors["neighborhood"]}
            helperText={editingDataErrors["neighborhood"]}
          />
        )}
        {visibleFields.includes("state") && (
          <Autocomplete
            disablePortal
            disabled={!isEditing}
            value={{ value: editingData.state_id || "", label: editingData.state_id || "" }}
            onChange={(_, newValue) => handleEditTitular({ ...editingData, state_id: newValue?.value || "" })}
            options={locationOptions.map(row => ({ value: row.state, label: row.state })).sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' }))}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }}
            renderInput={(params) => <TextField {...params} label={`Estado${requiredFields.includes("state") ? '*' : ''}`} error={!!editingDataErrors["state_id"]} helperText={editingDataErrors["state_id"]} />}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
          />
        )}
        {visibleFields.includes("city") && (
          <Autocomplete
            disablePortal
            disabled={!isEditing}
            value={{ value: editingData.city_id || "", label: editingData.city_id || "" }}
            onChange={(_, newValue) => handleEditTitular({ ...editingData, city_id: newValue?.value || "" })}
            options={locationOptions.find(row => row.state === editingData.state_id)?.cities.map(row => ({ value: row, label: row }))?.sort((a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' })) ?? []}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            style={{ minWidth: "260px", flex: "1 1 calc((100% - 260px) / 4)" }}
            renderInput={(params) => <TextField {...params} label={`Cidade${requiredFields.includes("city") ? '*' : ''}`} error={!!editingDataErrors["city_id"]} helperText={editingDataErrors["city_id"]} />}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
          />
        )}
        {/* <TextField
          label="Código da cidade"
          value={editingData.city_number}
          onChange={(e) => handleEditTitular({ ...editingData, city_number: e.target.value })}
          disabled={!isEditing}
          error={!!editingDataErrors["city_number"]}
          helperText={editingDataErrors["city_number"]}
        /> */}
      </div>
      <Subtitle>Confirmar benefícios</Subtitle>
      <span style={{ fontWeight: "500" }}>Selecione os benefícios do titular</span>
      <div style={{ display: "flex", flexDirection: "row", gap: "24px" }}>
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <Checkbox
            size="small"
            checked={editingData.employee_product_type.includes("NONE")}
            disabled={!isEditing}
            sx={{ "&.Mui-disabled svg": { fill: "rgb(112, 121, 116)" }}}
            onChange={() => !editingData.employee_product_type.includes("NONE") ? handleEditTitular({ ...editingData, employee_product_type: ["NONE"] }) : handleEditTitular({ ...editingData, employee_product_type: [] })} 
          />
          <span style={{ color: !isEditing ? "rgb(112, 121, 116)" : "initial" }}>Não quero optar pelos benefícios</span>
        </div>
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <Checkbox
            size="small"
            checked={editingData.employee_product_type.includes("HEALTH")}
            disabled={!isEditing}
            sx={{ "&.Mui-disabled svg": { fill: "rgb(112, 121, 116)" }}}
            onChange={() => !editingData.employee_product_type.includes("HEALTH") ? handleEditTitular({ ...editingData, employee_product_type: [...editingData.employee_product_type.filter(p => p !== "NONE"), "HEALTH"] }) : handleEditTitular({ ...editingData, employee_product_type: editingData.employee_product_type.filter(p => p !== "HEALTH") })} 
          />
          <span style={{ color: !isEditing ? "rgb(112, 121, 116)" : "initial" }}>Saúde</span>
        </div>
        {/* <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <Checkbox
            size="small"
            checked={editingData.employee_product_type.includes("LIFE")}
            disabled={!isEditing}
            sx={{ "&.Mui-disabled svg": { fill: "rgb(112, 121, 116)" }}}
            onChange={() => !editingData.employee_product_type.includes("LIFE") ? handleEditTitular({ ...editingData, employee_product_type: [...editingData.employee_product_type.filter(p => p !== "NONE"), "LIFE"] }) : handleEditTitular({ ...editingData, employee_product_type: editingData.employee_product_type.filter(p => p !== "LIFE") })} 
          />
          <span style={{ color: !isEditing ? "rgb(112, 121, 116)" : "initial" }}>Vida</span>
        </div> */}
        {/* <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <Checkbox
            size="small"
            checked={editingData.employee_product_type.includes("ODONTO")}
            disabled={!isEditing}
            sx={{ "&.Mui-disabled svg": { fill: "rgb(112, 121, 116)" }}}
            onChange={() => !editingData.employee_product_type.includes("ODONTO") ? handleEditTitular({ ...editingData, employee_product_type: [...editingData.employee_product_type.filter(p => p !== "NONE"), "ODONTO"] }) : handleEditTitular({ ...editingData, employee_product_type: editingData.employee_product_type.filter(p => p !== "ODONTO") })} 
          />
          <span style={{ color: !isEditing ? "rgb(112, 121, 116)" : "initial" }}>Odonto</span>
        </div> */}
      </div>
      {editingDataErrors["employee_product_type"] && <FormHelperText error style={{ marginTop: "-20px", marginLeft: "12px" }}>{editingDataErrors["employee_product_type"]}</FormHelperText>}
      <span style={{ fontWeight: "500" }}>Selecione os benefícios dos dependentes</span>
      <div style={{ display: "flex", flexDirection: "row", gap: "24px" }}>
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <Checkbox
            size="small"
            checked={editingData.dependent_product_type.includes("NONE")}
            disabled={!isEditing}
            sx={{ "&.Mui-disabled svg": { fill: "rgb(112, 121, 116)" }}}
            onChange={() => !editingData.dependent_product_type.includes("NONE") ? handleEditTitular({ ...editingData, dependent_product_type: ["NONE"] }) : handleEditTitular({ ...editingData, dependent_product_type: [] })}
          />
          <span style={{ color: !isEditing ? "rgb(112, 121, 116)" : "initial" }}>Não tenho dependentes</span>
        </div>
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <Checkbox
            size="small"
            checked={editingData.dependent_product_type.includes("HEALTH")}
            disabled={!isEditing}
            sx={{ "&.Mui-disabled svg": { fill: "rgb(112, 121, 116)" }}}
            onChange={() => !editingData.dependent_product_type.includes("HEALTH") ? handleEditTitular({ ...editingData, dependent_product_type: [...editingData.dependent_product_type.filter(p => p !== "NONE"), "HEALTH"] }) : handleEditTitular({ ...editingData, dependent_product_type: editingData.dependent_product_type.filter(p => p !== "HEALTH") })} 
          />
          <span style={{ color: !isEditing ? "rgb(112, 121, 116)" : "initial" }}>Saúde</span>
        </div>
        {/* <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <Checkbox
            size="small"
            checked={editingData.dependent_product_type.includes("LIFE")}
            disabled={!isEditing}
            sx={{ "&.Mui-disabled svg": { fill: "rgb(112, 121, 116)" }}}
            onChange={() => !editingData.dependent_product_type.includes("LIFE") ? handleEditTitular({ ...editingData, dependent_product_type: [...editingData.dependent_product_type.filter(p => p !== "NONE"), "LIFE"] }) : handleEditTitular({ ...editingData, dependent_product_type: editingData.dependent_product_type.filter(p => p !== "LIFE") })} 
          />
          <span style={{ color: !isEditing ? "rgb(112, 121, 116)" : "initial" }}>Vida</span>
        </div> */}
        {/* <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <Checkbox
            size="small"
            checked={editingData.dependent_product_type.includes("ODONTO")}
            disabled={!isEditing}
            sx={{ "&.Mui-disabled svg": { fill: "rgb(112, 121, 116)" }}}
            onChange={() => !editingData.dependent_product_type.includes("ODONTO") ? handleEditTitular({ ...editingData, dependent_product_type: [...editingData.dependent_product_type.filter(p => p !== "NONE"), "ODONTO"] }) : handleEditTitular({ ...editingData, dependent_product_type: editingData.dependent_product_type.filter(p => p !== "ODONTO") })} 
          />
          <span style={{ color: !isEditing ? "rgb(112, 121, 116)" : "initial" }}>Odonto</span>
        </div> */}
      </div>
      {editingDataErrors["dependent_product_type"] && <FormHelperText error style={{ marginTop: "-20px", marginLeft: "12px" }}>{editingDataErrors["dependent_product_type"]}</FormHelperText>}
      <div style={{ width: "100%", display: "flex", justifyContent: "end" }}>
        {!isEditing && (
          <CustomButton variant="contained" color="error" disabled={isEditing} onClick={handleNext}>Próximo</CustomButton>
        )}
        {isEditing && (
          <>
            <CustomButton variant="text" color="error" onClick={handleCancel}>Cancelar</CustomButton>
            <CustomButton variant="contained" onClick={handleSave} style={{ width: "fit-content" }}>Salvar edições</CustomButton>
          </>
        )}
      </div>
    </>
  );
}
